import { type CSSProperties } from "react";

// needed for row & cell level scope DnD setup
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { flexRender, type Header } from "@tanstack/react-table";
import { AlignJustify } from "lucide-react";
import type { DataTableColumnMeta } from "./DataTable";
import { TableHeaderContextMenu } from "./TableHeaderContextMenu";
import { cn } from "@/lib/utils/cn";

export function DraggableTableHeader<T>({
  header,
}: {
  header: Header<T, unknown>;
}) {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    });

  const style: CSSProperties = {
    opacity: isDragging ? 0.6 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    // Do not animate `width` here: combined with table layout it causes visible shrink/grow while reordering.
    transition: isDragging ? "none" : "transform 200ms ease-in-out",
    whiteSpace: "nowrap",
    minWidth: header.column.getSize(),
    zIndex: isDragging ? 1 : 0,
  };
  const meta = header.column.columnDef.meta as DataTableColumnMeta | undefined;

  return (
    <TableHeaderContextMenu
      header={header}
      trigger={
        <th colSpan={header.colSpan} ref={setNodeRef} style={style}>
          <span
            className={cn(
              "flex h-12 items-center px-4 whitespace-nowrap",
              meta?.headerAlign === "end" && "justify-end",
            )}
          >
            <button type="button" {...attributes} {...listeners}>
              <AlignJustify className="mr-2 w-4 opacity-80" />
            </button>
            {header.isPlaceholder
              ? null
              : flexRender(header.column.columnDef.header, header.getContext())}
          </span>
        </th>
      }
    />
  );
}
